import WorkorderChecklistFooter from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistFooter';
import { useWorkorderChecklistContext } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { DefaultRouteTabs, InfiniteTable } from '@/components/_common';
import { useAccessoryList } from '@/core/domain/people/Accessory/Accessory.hooks';
import { Accessory } from '@/core/domain/people/Accessory/Accessory.types';
import {
  useWorkOrderChecklistAccessoryCreate,
  useWorkOrderChecklistAccessoryList,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { WorkOrderChecklistAccessoryItem } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import useChecklistValidRoutes from '@/hooks/useChecklistValidRoutes';
import { PageProps } from '@/types/PageProps';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Form, Input, message } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

type AccessoryFormItem = Accessory & { comment?: string };

const WorkorderChecklistAccessoriesPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl();
  const { checklistItemId, checklistItemType, workorderId, boschChecklist } =
    useWorkorderChecklistContext();
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const accessoryList = useAccessoryList({
    active: true,
    checkList: true,
    rows: 30,
  });
  const accessories = flatInfiniteData(accessoryList.data);
  const [form] = Form.useForm<{
    items: AccessoryFormItem[];
  }>();

  const checklistAccessories = useWorkOrderChecklistAccessoryList({
    diagnosticId:
      checklistItemType == WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType == WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });

  const checklistAccessoryCreate = useWorkOrderChecklistAccessoryCreate();

  const routeParent = useChecklistValidRoutes({
    route,
    checklistItemType,
    workorderId,
    boschChecklist,
  });

  useEffect(() => {
    if (accessories) {
      form.setFieldValue(
        'items',
        accessories.map((item) => {
          const accessoryItem =
            !!checklistAccessories?.data &&
            checklistAccessories?.data?.find(
              (dataItem) => dataItem.accessoryId === item.id,
            );

          if (accessoryItem) {
            return { ...item, comment: accessoryItem.comment };
          }

          return item;
        }),
      );

      setSelectedKeys((prev) =>
        checklistAccessories.data
          ? [
              ...(checklistAccessories.data?.map((item) => item.accessoryId) ||
                []),
              ...prev,
            ]
          : prev,
      );
    }
  }, [checklistAccessories.dataUpdatedAt, accessoryList.dataUpdatedAt]);

  useEffect(() => {
    const items = form.getFieldValue('items') as AccessoryFormItem[];

    form.setFieldValue(
      'items',
      items.map((item) => ({
        ...item,
        comment: !selectedKeys.includes(item.id) ? undefined : item.comment,
      })),
    );
  }, [selectedKeys]);

  const columns: ColumnsType<Accessory> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
    },
    {
      dataIndex: 'comment',
      title: 'Comentário',
      render: (_, row, index) => (
        <>
          <Form.Item hidden name={[index, 'id']} noStyle>
            <Input />
          </Form.Item>
          <Form.Item name={[index, 'comment']} noStyle>
            <Input disabled={!selectedKeys.includes(row.id)} />
          </Form.Item>
        </>
      ),
    },
  ];

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      const accessories = selectedKeys.map<WorkOrderChecklistAccessoryItem>(
        (key) => {
          const accessoryFormItem = values.items.find(
            (valueItem) => valueItem.id === key,
          );

          return {
            accessoryId: Number(accessoryFormItem?.id),
            comment: accessoryFormItem?.comment,
          };
        },
      );

      if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
        await checklistAccessoryCreate.mutateAsync({
          accessories,
          diagnosticId: checklistItemId,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
        await checklistAccessoryCreate.mutateAsync({
          accessories,
          workOrderComplementId: checklistItemId,
        });
      }
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any;

      if (!error.isAxioError) {
        message.warn(error.message);
      }
    }
  }

  return (
    <DefaultRouteTabs
      route={routeParent}
      baseKey={String(checklistItemId)}
      basePath={`/app/movements/workorderChecklist/${checklistItemId}`}
      extra={
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={checklistAccessoryCreate.isLoading}
          block
        >
          Salvar acessórios
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form}>
        <Form.List name="items">
          {() => (
            <InfiniteTable
              dataSource={form.getFieldValue('items')}
              pagination={false}
              loading={accessoryList.isLoading}
              rowKey={(row) => row.id}
              columns={columns}
              rowSelection={{
                selectedRowKeys: selectedKeys,
                onChange: (keys: unknown) => setSelectedKeys(keys as number[]),
              }}
              fetchNextPage={accessoryList.fetchNextPage}
              hasNextPage={accessoryList.hasNextPage}
            />
          )}
        </Form.List>
      </Form>

      <WorkorderChecklistFooter />
    </DefaultRouteTabs>
  );
};

export default WorkorderChecklistAccessoriesPage;
